/**
 * PujcovnaUhradyPage — the overview of the úhrady (`/pujcovna/uhrady`, a tab of the module for every reader, the replacement of the legacy "Úhrady"
 * pane): every payment credited to a reservation, newest first — what, when, how (through the gateway or at the branch) and who recorded it — with a
 * week (stepped with the arrows, or all of them) and a source filter and the count and sum of everything the filter matches. Read-only: taking a payment and refunding one stay on the
 * reservation (Platby) and in the queue (Platby k řešení, Správa).
 */

import { useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Badge } from '@/components/Badge';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Chip } from '@/components/Chip';
import { Crown } from '@/components/Crown';
import { Icon } from '@/components/Icon';
import { IconButton } from '@/components/IconButton';
import { Menu } from '@/components/Menu';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { SegmentedControl } from '@/components/SegmentedControl';
import { Shell } from '@/components/Shell';
import { Skeleton } from '@/components/Skeleton';
import { Toolband } from '@/components/Toolband';
import { formatInt } from '@/lib/format';
import { addDays, pragueToday } from '@/lib/prague-date';
import { useModuleAccess } from '@/lib/permissions';
import { useUhradyQuery, type UhradaPrehled, type UhradyFilter } from './api';
import { datumCas } from './brana-helpers';
import { PujcovnaSubtabs } from './PujcovnaSubtabs';
import { pondeliTydne, tydenText, zpusobUhrady } from './platby-helpers';

type Zdroj = UhradyFilter['zdroj'];

type Rozsah = 'tyden' | 'vse';

const ZDROJE: Array<{ value: Zdroj; label: string }> = [
  { value: '', label: 'Všechny způsoby' },
  { value: 'platba', label: 'Přes bránu' },
  { value: 'rucne', label: 'Na pobočce' },
  { value: 'import', label: 'Původní systém' },
];

export function PujcovnaUhradyPage() {
  const { can, resolved } = useModuleAccess();
  const canRead = resolved && can('pujcovna', 'read');
  const [zdroj, setZdroj] = useState<Zdroj>('');
  const [rozsah, setRozsah] = useState<Rozsah>('tyden');
  const [pondeli, setPondeli] = useState(() => pondeliTydne(pragueToday()));
  const filtr = useMemo<UhradyFilter>(
    () => ({ od: rozsah === 'tyden' ? pondeli : '', do: rozsah === 'tyden' ? addDays(pondeli, 6) : '', zdroj }),
    [rozsah, pondeli, zdroj],
  );
  const query = useUhradyQuery(filtr, canRead);
  // offset paging over a list that grows at the top can return a row twice — keep the first occurrence
  const rows = useMemo(() => {
    const videno = new Set<number>();
    return (query.data?.pages.flatMap((p) => p.uhrady) ?? []).filter((u) => !videno.has(u.id) && videno.add(u.id));
  }, [query.data]);
  const prvni = query.data?.pages[0];

  const columns: DataTableColumn<UhradaPrehled>[] = [
    {
      id: 'kdy',
      header: 'Datum',
      width: '150px',
      priority: 'primary',
      cell: (u) => <span className="font-mono text-[12px] text-ink-2">{datumCas(u.vytvoreno_at)}</span>,
    },
    {
      id: 'rezervace',
      header: 'Rezervace',
      width: 'minmax(170px, 1.2fr)',
      priority: 'secondary',
      cell: (u) => (
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="font-mono text-[12px] text-ink">№ {u.id_rezervace}</span>
          <span className="truncate text-[12.5px] text-ink-2">{u.zakaznik_nazev ?? '—'}</span>
        </span>
      ),
    },
    {
      id: 'castka',
      header: 'Částka',
      width: '120px',
      align: 'right',
      priority: 'primary',
      cell: (u) => <span className="font-mono text-[13px] text-ink">{formatInt(u.castka)} Kč</span>,
    },
    {
      id: 'zpusob',
      header: 'Způsob',
      width: 'minmax(150px, 1fr)',
      priority: 'secondary',
      cell: (u) => (
        <Badge tone={u.zdroj === 'platba' ? 'accent' : 'neutral'} size="sm">
          {zpusobUhrady(u)}
        </Badge>
      ),
    },
    {
      id: 'uzivatel',
      header: 'Zadal',
      width: 'minmax(130px, 1fr)',
      priority: 'meta',
      cell: (u) => <span className="text-[12.5px] text-ink-2">{u.uzivatel ?? '—'}</span>,
    },
    {
      id: 'poznamka',
      header: 'Poznámka',
      width: 'minmax(160px, 1.2fr)',
      priority: 'meta',
      cell: (u) => (
        <span title={u.poznamka} className="line-clamp-2 text-[12px] text-ink-2">
          {u.poznamka || '—'}
        </span>
      ),
    },
  ];

  return (
    <Shell rail={<AppRail active="pujcovna" />}>
      <Crown>
        <Breadcrumb segments={[{ label: 'Půjčovna', href: '/pujcovna' }, { label: 'Úhrady' }]} />
        <Crown.Row className="mt-3">
          <DisplayHeading>
            Úhrady,
            <br />
            <em>co přišlo na účet.</em>
          </DisplayHeading>
        </Crown.Row>
        <PujcovnaSubtabs active="uhrady" />
      </Crown>
      <Toolband>
        <Menu>
          <Menu.Trigger asChild>
            <Chip icon={<Icon name="filter" size={12} />} active={zdroj !== ''} data-testid="uhrady-zdroj">
              {ZDROJE.find((z) => z.value === zdroj)?.label}
            </Chip>
          </Menu.Trigger>
          <Menu.Content>
            <Menu.Label>Způsob úhrady</Menu.Label>
            {ZDROJE.map((z) => (
              <Menu.CheckItem key={z.value} checked={zdroj === z.value} onCheckedChange={(checked) => checked && setZdroj(z.value)}>
                {z.label}
              </Menu.CheckItem>
            ))}
          </Menu.Content>
        </Menu>
        <SegmentedControl<Rozsah>
          aria-label="Období"
          value={rozsah}
          onChange={setRozsah}
          options={[
            { value: 'tyden', label: 'Týden' },
            { value: 'vse', label: 'Všechny' },
          ]}
        />
        {rozsah === 'tyden' && (
          <span className="flex items-center gap-2" data-testid="uhrady-tyden-nav">
            <IconButton size="sm" aria-label="Předchozí týden" onClick={() => setPondeli((p) => addDays(p, -7))}>
              <Icon name="chevronL" size={12} />
            </IconButton>
            <span data-testid="uhrady-tyden" className="min-w-[170px] text-center font-mono text-[12px] tracking-[0.04em] text-ink">
              {tydenText(pondeli)}
            </span>
            <IconButton size="sm" aria-label="Další týden" onClick={() => setPondeli((p) => addDays(p, 7))}>
              <Icon name="chevronR" size={12} />
            </IconButton>
            <Button variant="secondary" size="sm" onClick={() => setPondeli(pondeliTydne(pragueToday()))}>
              Tento týden
            </Button>
          </span>
        )}
        <Toolband.Spacer />
        <span data-testid="uhrady-souhrn" className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-ink-3">
          {prvni?.celkem != null && prvni.soucet != null ? `${formatInt(prvni.celkem)} úhrad · ${formatInt(prvni.soucet)} Kč připsáno` : '—'}
        </span>
      </Toolband>
      <div data-testid="uhrady-tool" className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-8 pb-8 pt-5">
        {!resolved ? (
          <Skeleton className="h-10 w-full" />
        ) : !canRead ? (
          <EmptyState eyebrow="403" title="Úhrady vyžadují přístup k modulu Půjčovna." description="Pro přístup požádejte správce firmy." />
        ) : query.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : query.isError && rows.length === 0 ? (
          <EmptyState eyebrow="Chyba" title="Úhrady se nepodařilo načíst" description="Zkuste to prosím znovu za chvíli." />
        ) : rows.length === 0 ? (
          <EmptyState eyebrow="Bez záznamů" title="V tomto výběru nejsou žádné úhrady" description="Přejděte na jiný týden nebo změňte způsob úhrady." />
        ) : (
          <div className="flex flex-col gap-3">
            <DataTable<UhradaPrehled> aria-label="Úhrady rezervací" variant="bordered" columns={columns} data={rows} getRowId={(u) => String(u.id)} />
            {query.hasNextPage && (
              <div>
                <Button variant="secondary" size="sm" disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
                  Načíst další
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </Shell>
  );
}
